import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  IconButton,
  Tooltip,
  TablePagination,
  Alert,
  Divider,
  Stack,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  Tabs,
  Tab,
  Badge,
  Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import api from '../../services/api';
import CustomerRateCardTab from './CustomerRateCardTab';

// v2-fix-numeric-types
interface Rate {
  id: number;
  destination: string;
  destination_name: string;
  destination_group: string;
  sell_rate: number;
  sell_rate_offpeak: number;
  sell_rate_weekend: number;
  buy_rate: number;
  buy_rate_offpeak: number;
  connect_fee: number;
  billing_increment: number;
  min_duration: number;
  carrier_id: number;
  carrier_name: string;
  backup_carrier_id?: string | null;
  backup_carrier_name?: string | null;
  plan_id: number;
  plan_name: string;
  plan_type: 'retail' | 'wholesale' | 'reseller';
  currency: string;
  margin: number;
  margin_pct: number;
  created_at: string;
}

interface Stats {
  total_rates: number;
  total_plans: number;
  total_groups: number;
  avg_margin: number;
  negative_margin_count: number;
  min_sell: number;
  max_sell: number;
}

interface Plan {
  id: number;
  name: string;
  plan_type: string;
}

interface Carrier {
  id: number;
  name: string;
}

interface Tenant {
  id: string;
  name: string;
  company_name: string;
  tier: string;
  status: string;
  rate_plan_id: string | null;
  routing_profile_id: string | null;
}

type SortField = keyof Rate;
type SortDir = 'asc' | 'desc';

const planTypeColor = (t: string) =>
  t === 'retail' ? 'primary' : t === 'wholesale' ? 'warning' : 'secondary';

const marginColor = (margin: number): string => {
  if (margin > 0) return '#2e7d32';
  if (margin < 0) return '#c62828';
  return '#757575';
};

const marginPctColor = (pct: number): string => {
  if (pct > 30) return '#1b5e20';
  if (pct >= 10) return '#2e7d32';
  if (pct >= 0) return '#e65100';
  return '#c62828';
};

const fmt4 = (n: any) => (n === null || n === undefined) ? '—' : (parseFloat(n) || 0).toFixed(4);

export const RateCardPage: React.FC = () => {
  const [rates, setRates] = useState<Rate[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<Stats | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [planType, setPlanType] = useState<string>('');
  const [planId, setPlanId] = useState<string>('');
  const [carrierId, setCarrierId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [sortField, setSortField] = useState<SortField>('destination');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const [editOpen, setEditOpen] = useState(false);
  const [editRate, setEditRate] = useState<Rate | null>(null);
  const [editForm, setEditForm] = useState<Partial<Rate>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Assign-to-customers state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignRate, setAssignRate] = useState<Rate | null>(null);
  const [assignSelectedTenants, setAssignSelectedTenants] = useState<Tenant[]>([]);
  const [assignPrimaryCarrierId, setAssignPrimaryCarrierId] = useState('');
  const [assignSecondaryCarrierId, setAssignSecondaryCarrierId] = useState('');
  const [assignTertiaryCarrierId, setAssignTertiaryCarrierId] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignResult, setAssignResult] = useState<any>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);

  // Apply-trunk (reflect a trunk's buy rates onto a plan) + margin guard
  const [applyingTrunk, setApplyingTrunk] = useState(false);
  const [applyResult, setApplyResult] = useState<any>(null);
  const [applyMarkup, setApplyMarkup] = useState('0');

  // Monitoring tab state
  const [activeTab, setActiveTab] = useState(0);
  const [failoverEvents, setFailoverEvents] = useState<any[]>([]);
  const [failoverLoading, setFailoverLoading] = useState(false);
  const [failoverFilter, setFailoverFilter] = useState('');

  const loadFailoverEvents = useCallback(async () => {
    setFailoverLoading(true);
    try {
      const { data: res } = await api.get('/routing/failover-events');
      setFailoverEvents(res.data || []);
    } catch { }
    finally { setFailoverLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === 1) loadFailoverEvents(); }, [activeTab, loadFailoverEvents]);

  const loadTenants = useCallback(async () => {
    try {
      const res = await api.get('/tenants', { params: { tier: 'customer', limit: 500 } });
      setTenants(res.data?.data ?? res.data ?? []);
    } catch { }
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get('/rates/consolidated/stats');
      setStats(res.data);
    } catch {
      // non-fatal
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      if (planType) params.plan_type = planType;
      if (planId) params.plan_id = planId;
      if (carrierId) params.carrier_id = carrierId;
      if (search) params.search = search;
      const res = await api.get('/rates/consolidated', { params });
      setRates(res.data.data);
      setTotal(res.data.total);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load rates');
    } finally {
      setLoading(false);
    }
  }, [planType, planId, carrierId, search, page, rowsPerPage]);

  const fetchFilters = useCallback(async () => {
    try {
      const [plansRes, carriersRes] = await Promise.all([
        api.get('/rates/plans'),
        api.get('/carriers'),
      ]);
      setPlans(plansRes.data?.data ?? plansRes.data ?? []);
      setCarriers(carriersRes.data?.data ?? carriersRes.data ?? []);
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    fetchFilters();
    fetchStats();
  }, [fetchFilters, fetchStats]);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedRates = [...rates].sort((a, b) => {
    const av = a[sortField];
    const bv = b[sortField];
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    if (typeof av === 'number' && typeof bv === 'number') {
      return sortDir === 'asc' ? av - bv : bv - av;
    }
    return sortDir === 'asc'
      ? String(av).localeCompare(String(bv))
      : String(bv).localeCompare(String(av));
  });

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
    );
  };

  const openEdit = (rate: Rate) => {
    setEditRate(rate);
    setEditForm({
      buy_rate: rate.buy_rate,
      sell_rate: rate.sell_rate,
      buy_rate_offpeak: rate.buy_rate_offpeak,
      sell_rate_offpeak: rate.sell_rate_offpeak,
      sell_rate_weekend: rate.sell_rate_weekend,
      connect_fee: rate.connect_fee,
      billing_increment: rate.billing_increment,
      min_duration: rate.min_duration,
      carrier_id: rate.carrier_id ? String(rate.carrier_id) : '',
      backup_carrier_id: rate.backup_carrier_id ? String(rate.backup_carrier_id) : '',
    } as any);
    setSaveError(null);
    setEditOpen(true);
  };

  const handleEditChange = (field: keyof Rate, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const liveMargin =
    editForm.sell_rate !== undefined && editForm.buy_rate !== undefined
      ? (editForm.sell_rate as number) - (editForm.buy_rate as number)
      : null;

  const liveMarginPct =
    liveMargin !== null && editForm.buy_rate
      ? (liveMargin / (editForm.buy_rate as number)) * 100
      : null;

  const handleSave = async () => {
    if (!editRate) return;
    setSaving(true);
    setSaveError(null);
    try {
      // The consolidated list aliases rate_peak/offpeak/weekend as sell_rate*; the rates API expects the column names.
      const { sell_rate, sell_rate_offpeak, sell_rate_weekend, ...rest } = editForm as any;
      const payload: any = { ...rest };
      if (sell_rate !== undefined) payload.rate_peak = sell_rate;
      if (sell_rate_offpeak !== undefined) payload.rate_offpeak = sell_rate_offpeak;
      if (sell_rate_weekend !== undefined) payload.rate_weekend = sell_rate_weekend;
      await api.put(`/rates/${editRate.id}`, payload);
      setEditOpen(false);
      fetchRates();
      fetchStats();
    } catch (e: any) {
      setSaveError(e?.response?.data?.message || 'Failed to save rate');
    } finally {
      setSaving(false);
    }
  };

  const openAssign = (rate: Rate) => {
    setAssignRate(rate);
    setAssignSelectedTenants([]);
    setAssignPrimaryCarrierId(rate.carrier_id ? String(rate.carrier_id) : '');
    setAssignSecondaryCarrierId('');
    setAssignTertiaryCarrierId('');
    setAssignResult(null);
    setAssignOpen(true);
  };

  const handleAssignSubmit = async () => {
    if (!assignRate || assignSelectedTenants.length === 0 || !assignPrimaryCarrierId) return;
    setAssignLoading(true);
    try {
      const carriersPayload = [
        { carrier_id: assignPrimaryCarrierId, priority: 1 },
        ...(assignSecondaryCarrierId ? [{ carrier_id: assignSecondaryCarrierId, priority: 2 }] : []),
        ...(assignTertiaryCarrierId ? [{ carrier_id: assignTertiaryCarrierId, priority: 3 }] : []),
      ];
      const res = await api.post('/routing/assign-destination', {
        destination_prefix: assignRate.destination,
        destination_name: assignRate.destination_name,
        plan_id: assignRate.plan_id ? String(assignRate.plan_id) : undefined,
        carriers: carriersPayload,
        tenant_ids: assignSelectedTenants.map(t => t.id),
      });
      setAssignResult(res.data);
    } catch (e: any) {
      setAssignResult({ error: e?.response?.data?.message || 'Assignment failed' });
    } finally {
      setAssignLoading(false);
    }
  };

  const handleExportCSV = () => {
    const headers = [
      'Destination',
      'Name',
      'Group',
      'Plan',
      'Plan Type',
      'Currency',
      'Buy Rate',
      'Sell Rate',
      'Margin',
      'Margin %',
      'Billing Inc',
      'Connect Fee',
    ];
    const rows = sortedRates.map(r => [
      r.destination,
      r.destination_name,
      r.destination_group,
      r.plan_name,
      r.plan_type,
      r.currency,
      r.buy_rate,
      r.sell_rate,
      r.margin,
      r.margin_pct,
      r.billing_increment,
      r.connect_fee,
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rate-card-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const thStyle = {
    cursor: 'pointer',
    userSelect: 'none' as const,
    whiteSpace: 'nowrap' as const,
    fontWeight: 600,
    fontSize: '0.75rem',
  };

  const applyTrunkBuyRates = async () => {
    if (!planId || !carrierId) return;
    const carrierName = carriers.find(c => String(c.id) === String(carrierId))?.name || 'trunk';
    const planName = plans.find(p => String(p.id) === String(planId))?.name || 'plan';
    setApplyingTrunk(true); setApplyResult(null);
    try {
      const { data } = await api.post(`/rates/plans/${planId}/apply-trunk`, { carrier_id: carrierId, markup_percent: Number(applyMarkup) || 0 });
      setApplyResult({ ...data, carrierName, planName });
      await fetchRates(); await fetchStats();
    } catch (e: any) {
      setApplyResult({ error: e.response?.data?.message || 'Apply failed', carrierName, planName });
    } finally { setApplyingTrunk(false); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Rate Card
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Consolidated buy/sell rates across all plans
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
          disabled={rates.length === 0}
        >
          Export CSV
        </Button>
      </Stack>

      {statsLoading ? (
        <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
      ) : stats ? (
        <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
          <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, minWidth: 140 }}>
            <Typography variant="caption" color="text.secondary">
              Total Destinations
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {stats.total_rates.toLocaleString()}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary">
              Plans
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {stats.total_plans}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, minWidth: 140 }}>
            <Typography variant="caption" color="text.secondary">
              Avg Margin
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              color={marginColor(stats.avg_margin)}
            >
              {fmt4(stats.avg_margin)}
            </Typography>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              px: 2.5,
              py: 1.5,
              minWidth: 160,
              borderColor: stats.negative_margin_count > 0 ? 'error.main' : undefined,
              bgcolor: stats.negative_margin_count > 0 ? 'error.50' : undefined,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Negative Margin Rates
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {stats.negative_margin_count > 0 && (
                <WarningAmberIcon sx={{ color: 'error.main', fontSize: 18 }} />
              )}
              <Typography
                variant="h6"
                fontWeight={700}
                color={stats.negative_margin_count > 0 ? 'error.main' : 'text.primary'}
              >
                {stats.negative_margin_count}
              </Typography>
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary">
              Sell Range
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {fmt4(stats.min_sell)} – {fmt4(stats.max_sell)}
            </Typography>
          </Paper>
        </Stack>
      ) : null}


      {/* Tab bar */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Rate Card" />
        <Tab
          label="Failover Monitor"
          icon={
            <Badge badgeContent={failoverEvents.filter(e => e.status === 'active').length} color="error" invisible={failoverEvents.filter(e => e.status === 'active').length === 0}>
              <MonitorHeartIcon fontSize="small" />
            </Badge>
          }
          iconPosition="start"
        />
        <Tab label="Customer Rate Cards" />
      </Tabs>

      {activeTab === 2 ? (
        <CustomerRateCardTab />
      ) : activeTab === 1 ? (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <TextField
              size="small" placeholder="Filter events…"
              value={failoverFilter} onChange={e => setFailoverFilter(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.disabled', fontSize: 18 }} /> }}
              sx={{ width: 300 }}
            />
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadFailoverEvents}>Refresh</Button>
          </Box>
          {failoverLoading ? <LinearProgress sx={{ mb: 2 }} /> : null}
          <Card variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Time', 'Customer', 'Destination', 'Primary (Failed)', '', 'Failover (Used)', 'Reason', 'Calls', 'Status', ''].map((h, i) => (
                    <TableCell key={i} sx={{ fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {failoverEvents
                  .filter(e => {
                    if (!failoverFilter) return true;
                    const f = failoverFilter.toLowerCase();
                    return (e.tenant_name || '').toLowerCase().includes(f) || (e.tenant_company || '').toLowerCase().includes(f)
                      || (e.destination_prefix || '').includes(f) || (e.primary_carrier_name || '').toLowerCase().includes(f)
                      || (e.failover_carrier_name || '').toLowerCase().includes(f);
                  })
                  .map((e: any) => (
                    <TableRow key={e.id} hover sx={e.status === 'active' ? { bgcolor: 'rgba(211,47,47,0.04)' } : {}}>
                      <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(e.created_at).toLocaleString()}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{e.tenant_company || e.tenant_name || '—'}</TableCell>
                      <TableCell><Box sx={{ fontFamily: 'monospace', fontWeight: 700 }}>+{e.destination_prefix}</Box></TableCell>
                      <TableCell><Box sx={{ px: 1, py: 0.3, borderRadius: 1, bgcolor: '#ffcdd2', color: '#b71c1c', fontWeight: 600, fontSize: 11, display: 'inline-block' }}>{e.primary_carrier_name || '—'}</Box></TableCell>
                      <TableCell><SwapHorizIcon sx={{ color: 'text.secondary', fontSize: 16 }} /></TableCell>
                      <TableCell><Box sx={{ px: 1, py: 0.3, borderRadius: 1, bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: 11, display: 'inline-block' }}>{e.failover_carrier_name || '—'}</Box></TableCell>
                      <TableCell sx={{ fontSize: 11, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.reason}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{e.call_count}</TableCell>
                      <TableCell>
                        <Chip label={e.status} size="small" color={e.status === 'active' ? 'error' : 'success'} />
                      </TableCell>
                      <TableCell>
                        {e.status === 'active' && (
                          <Tooltip title="Mark resolved">
                            <IconButton size="small" color="success" onClick={async () => { await api.put('/routing/failover-events/' + e.id + '/resolve', {}); loadFailoverEvents(); }}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={async () => { if (window.confirm('Delete event?')) { await api.delete('/routing/failover-events/' + e.id); loadFailoverEvents(); } }}>
                            <ArrowDownwardIcon fontSize="small" sx={{ transform: 'rotate(45deg)' }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                {failoverEvents.length === 0 && !failoverLoading && (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <MonitorHeartIcon sx={{ fontSize: 40, mb: 1, display: 'block', mx: 'auto', opacity: 0.3 }} />
                    No failover events recorded. Log events from the Routing page.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </Box>
      ) : (
        <Box>
      <Card variant="outlined" sx={{ mb: 0 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
            <ToggleButtonGroup
              value={planType}
              exclusive
              onChange={(_, v) => {
                setPlanType(v ?? '');
                setPage(0);
              }}
              size="small"
            >
              <ToggleButton value="">All</ToggleButton>
              <ToggleButton value="retail">Retail</ToggleButton>
              <ToggleButton value="wholesale">Wholesale</ToggleButton>
              <ToggleButton value="reseller">Reseller</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              select
              size="small"
              label="Plan"
              value={planId}
              onChange={e => {
                setPlanId(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All Plans</MenuItem>
              {plans.map(p => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Carrier"
              value={carrierId}
              onChange={e => {
                setCarrierId(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All Carriers</MenuItem>
              {carriers.map(c => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField size="small" label="Markup %" type="number" value={applyMarkup}
              onChange={e => setApplyMarkup(e.target.value)} sx={{ width: 100 }}
              inputProps={{ step: '1', min: 0 }}
              helperText="new dests" FormHelperTextProps={{ sx: { m: 0, fontSize: 10 } }} />
            <Tooltip title={(!planId || !carrierId)
              ? 'Select a Plan and a Carrier first — reflects that trunk’s buy rates onto the plan, seeds missing destinations (sell = buy + markup%), and flags any sell-below-buy'
              : 'Reflect the selected trunk’s buy rates onto the selected plan; new destinations get sell = buy + markup%'}>
              <span>
                <Button size="small" variant="outlined" color="secondary"
                  disabled={!planId || !carrierId || applyingTrunk}
                  onClick={applyTrunkBuyRates}>
                  {applyingTrunk ? 'Applying…' : 'Apply Trunk Buy Rates'}
                </Button>
              </span>
            </Tooltip>

            <TextField
              size="small"
              placeholder="Search destination or name…"
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 240 }}
            />

            <Tooltip title="Refresh">
              <IconButton onClick={() => { fetchRates(); fetchStats(); }} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {total.toLocaleString()} rates
            </Typography>
          </Stack>
        </Box>

        {applyResult && (
          <Alert
            severity={applyResult.error ? 'error' : (applyResult.violations > 0 ? 'error' : 'success')}
            sx={{ mx: 2, mt: 1 }}
            onClose={() => setApplyResult(null)}
          >
            {applyResult.error ? applyResult.error : (
              <>
                Applied <b>{applyResult.carrierName}</b> buy rates to <b>{applyResult.planName}</b> — {applyResult.updated} updated, {applyResult.inserted} destination(s) added, {applyResult.matched} now have a buy rate.
                {applyResult.violations > 0 ? (
                  <>
                    {' '}<b>⛔ STOP: {applyResult.violations} destination(s) have SELL below BUY (negative margin).</b>
                    {' '}Adjust the sell or buy rate, or route this plan to a trunk with a lower buy rate.
                    <Box sx={{ mt: 1, maxHeight: 170, overflow: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
                      {(applyResult.violation_list || []).slice(0, 100).map((v: any, i: number) => (
                        <div key={i}>
                          {v.destination}{v.destination_name ? ` (${v.destination_name})` : ''}: sell {Number(v.sell).toFixed(5)} &lt; buy {Number(v.buy).toFixed(5)} → margin {Number(v.margin).toFixed(5)}
                        </div>
                      ))}
                    </Box>
                  </>
                ) : ' All margins are positive. ✅'}
              </>
            )}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
            {error}
          </Alert>
        )}

        {loading && <LinearProgress />}

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={thStyle} onClick={() => handleSort('destination')}>
                  Destination <SortIcon field="destination" />
                </TableCell>
                <TableCell sx={thStyle} onClick={() => handleSort('destination_name')}>
                  Name / Group <SortIcon field="destination_name" />
                </TableCell>
                <TableCell sx={thStyle} onClick={() => handleSort('plan_name')}>
                  Plan <SortIcon field="plan_name" />
                </TableCell>
                <TableCell sx={thStyle} onClick={() => handleSort('currency')}>
                  Currency <SortIcon field="currency" />
                </TableCell>
                <TableCell sx={{ ...thStyle, color: '#1565c0' }} onClick={() => handleSort('buy_rate')}>
                  Buy Rate <SortIcon field="buy_rate" />
                </TableCell>
                <TableCell sx={{ ...thStyle, color: '#2e7d32' }} onClick={() => handleSort('sell_rate')}>
                  Sell Rate <SortIcon field="sell_rate" />
                </TableCell>
                <TableCell sx={thStyle} onClick={() => handleSort('margin')}>
                  Margin <SortIcon field="margin" />
                </TableCell>
                <TableCell sx={thStyle} onClick={() => handleSort('margin_pct')}>
                  Margin % <SortIcon field="margin_pct" />
                </TableCell>
                <TableCell sx={thStyle} onClick={() => handleSort('billing_increment')}>
                  Billing Inc <SortIcon field="billing_increment" />
                </TableCell>
                <TableCell sx={thStyle} onClick={() => handleSort('connect_fee')}>
                  Connect Fee <SortIcon field="connect_fee" />
                </TableCell>
                <TableCell sx={{ ...thStyle, cursor: 'default' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && sortedRates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No rates found
                  </TableCell>
                </TableRow>
              )}
              {sortedRates.map(rate => {
                const sellColor = rate.margin >= 0 ? '#2e7d32' : '#e65100';
                return (
                  <TableRow
                    key={rate.id}
                    hover
                    sx={{ '&:last-child td': { border: 0 } }}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontWeight={700}
                        fontSize="0.8rem"
                      >
                        {rate.destination}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize="0.8rem">
                        {rate.destination_name}
                      </Typography>
                      {rate.destination_group && (
                        <Typography variant="caption" color="text.secondary">
                          {rate.destination_group}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rate.plan_name}
                        color={planTypeColor(rate.plan_type) as any}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                      {rate.carrier_name && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.3 }}>
                          Route: {rate.carrier_name}{rate.backup_carrier_name ? ` → backup ${rate.backup_carrier_name}` : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize="0.78rem">
                        {rate.currency}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontSize="0.78rem"
                        color="#1565c0"
                      >
                        {fmt4(rate.buy_rate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontSize="0.78rem"
                        color={sellColor}
                        fontWeight={500}
                      >
                        {fmt4(rate.sell_rate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontSize="0.78rem"
                        fontWeight={700}
                        color={marginColor(rate.margin)}
                      >
                        {fmt4(rate.margin)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontSize="0.78rem"
                        fontWeight={600}
                        color={marginPctColor(rate.margin_pct)}
                      >
                        {rate.margin_pct != null ? `${parseFloat(String(rate.margin_pct)).toFixed(1)}%` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize="0.78rem">
                        {rate.billing_increment}s
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {rate.connect_fee > 0 ? (
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          fontSize="0.78rem"
                        >
                          {fmt4(rate.connect_fee)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled" fontSize="0.78rem">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit rate">
                        <IconButton size="small" onClick={() => openEdit(rate)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Assign to customers">
                        <IconButton size="small" color="primary" onClick={() => openAssign(rate)}>
                          <PersonAddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 250]}
        />
      </Card>
        </Box>
      )}

      {/* ── Assign Destination to Customers Dialog ── */}
      <Dialog open={assignOpen} onClose={() => { setAssignOpen(false); setAssignResult(null); }} maxWidth="md" fullWidth>
        <DialogTitle>
          Assign Destination to Customers
          {assignRate && (
            <Typography variant="body2" color="text.secondary" component="div">
              +{assignRate.destination} — {assignRate.destination_name} · {assignRate.plan_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {assignRate && (
            <Stack direction="row" spacing={3} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Destination</Typography>
                <Typography variant="h6" fontFamily="monospace" fontWeight={700}>+{assignRate.destination}</Typography>
                <Typography variant="body2" color="text.secondary">{assignRate.destination_name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Sell Rate</Typography>
                <Typography variant="h6" fontFamily="monospace" fontWeight={700} color="#2e7d32">{fmt4(assignRate.sell_rate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Buy Rate</Typography>
                <Typography variant="h6" fontFamily="monospace" fontWeight={700} color="#1565c0">{fmt4(assignRate.buy_rate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Rate Plan</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={assignRate.plan_name} size="small" color={planTypeColor(assignRate.plan_type) as any} />
                </Box>
              </Box>
            </Stack>
          )}

          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Routing — Carrier Priority</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              <TextField
                select fullWidth size="small" label="Primary Carrier *"
                value={assignPrimaryCarrierId}
                onChange={e => setAssignPrimaryCarrierId(e.target.value)}
              >
                <MenuItem value="">Select carrier…</MenuItem>
                {carriers.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField
                select fullWidth size="small" label="Secondary Carrier (failover)"
                value={assignSecondaryCarrierId}
                onChange={e => setAssignSecondaryCarrierId(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {carriers.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField
                select fullWidth size="small" label="Tertiary Carrier"
                value={assignTertiaryCarrierId}
                onChange={e => setAssignTertiaryCarrierId(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {carriers.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Select Customers
            {assignSelectedTenants.length > 0 && (
              <Chip label={`${assignSelectedTenants.length} selected`} size="small" sx={{ ml: 1 }} color="primary" />
            )}
          </Typography>
          <Autocomplete
            multiple
            options={tenants}
            getOptionLabel={t => (t as Tenant).company_name || (t as Tenant).name}
            value={assignSelectedTenants}
            onChange={(_, val) => setAssignSelectedTenants(val as Tenant[])}
            isOptionEqualToValue={(opt, val) => (opt as Tenant).id === (val as Tenant).id}
            renderInput={params => (
              <TextField {...params} size="small" placeholder="Search and select customers…" />
            )}
            renderTags={(val, getTagProps) =>
              (val as Tenant[]).map((t, idx) => (
                <Chip
                  label={t.company_name || t.name}
                  size="small"
                  color={t.routing_profile_id ? 'default' : 'warning'}
                  title={t.routing_profile_id ? 'Has routing profile' : 'No routing profile — will be auto-created'}
                  {...getTagProps({ index: idx })}
                />
              ))
            }
            renderOption={(props, t) => (
              <li {...props} key={(t as Tenant).id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{(t as Tenant).company_name || (t as Tenant).name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(t as Tenant).routing_profile_id ? '✓ has routing profile' : '⚡ routing profile will be created'}
                    {!(t as Tenant).rate_plan_id ? ' · rate plan will be linked' : ''}
                  </Typography>
                </Box>
              </li>
            )}
            sx={{ mb: 2 }}
          />

          {assignResult && (
            <Box sx={{ mt: 2 }}>
              {assignResult.error ? (
                <Alert severity="error">{assignResult.error}</Alert>
              ) : (
                <Alert severity={assignResult.failed > 0 ? 'warning' : 'success'} sx={{ mb: 1 }}>
                  {assignResult.succeeded} customer{assignResult.succeeded !== 1 ? 's' : ''} assigned successfully
                  {assignResult.failed > 0 ? ` · ${assignResult.failed} failed` : ''}.
                </Alert>
              )}
              {assignResult.results?.map((r: any) => (
                <Stack key={r.tenant_id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
                  {r.status === 'success'
                    ? <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
                    : <WarningAmberIcon sx={{ color: 'error.main', fontSize: 16 }} />
                  }
                  <Typography variant="body2" fontWeight={500}>{r.tenant_name}</Typography>
                  {r.profile_created && <Chip label="Profile created" size="small" color="info" variant="outlined" />}
                  {r.status === 'error' && (
                    <Typography variant="caption" color="error.main">{r.message}</Typography>
                  )}
                </Stack>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAssignOpen(false); setAssignResult(null); }}>
            {assignResult ? 'Close' : 'Cancel'}
          </Button>
          {!assignResult && (
            <Button
              variant="contained"
              onClick={handleAssignSubmit}
              disabled={assignLoading || assignSelectedTenants.length === 0 || !assignPrimaryCarrierId}
              startIcon={assignLoading ? <CircularProgress size={16} /> : <PersonAddIcon />}
            >
              {assignLoading
                ? 'Assigning…'
                : `Assign to ${assignSelectedTenants.length} Customer${assignSelectedTenants.length !== 1 ? 's' : ''}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Rate
          {editRate && (
            <Typography variant="body2" color="text.secondary" component="div">
              {editRate.destination} — {editRate.plan_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}

          {liveMargin !== null && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 2,
                bgcolor:
                  liveMargin > 0
                    ? 'success.50'
                    : liveMargin < 0
                    ? 'error.50'
                    : 'grey.50',
                borderColor:
                  liveMargin > 0
                    ? 'success.main'
                    : liveMargin < 0
                    ? 'error.main'
                    : 'divider',
              }}
            >
              <Stack direction="row" spacing={3} alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Margin
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    color={marginColor(liveMargin)}
                    fontFamily="monospace"
                  >
                    {fmt4(liveMargin)}
                  </Typography>
                </Box>
                {liveMarginPct !== null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Margin %
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      color={marginPctColor(liveMarginPct)}
                    >
                      {liveMarginPct.toFixed(2)}%
                    </Typography>
                  </Box>
                )}
                {liveMargin < 0 && (
                  <Alert severity="error" sx={{ py: 0, flex: 1 }} icon={<WarningAmberIcon />}>
                    Sell rate is below buy rate
                  </Alert>
                )}
              </Stack>
            </Paper>
          )}

          <Divider sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Peak Rates
            </Typography>
          </Divider>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Buy Rate"
                type="number"
                inputProps={{ step: '0.0001', min: 0 }}
                value={editForm.buy_rate ?? ''}
                onChange={e => handleEditChange('buy_rate', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Sell Rate"
                type="number"
                inputProps={{ step: '0.0001', min: 0 }}
                value={editForm.sell_rate ?? ''}
                onChange={e => handleEditChange('sell_rate', e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Off-Peak Rates
            </Typography>
          </Divider>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Buy Rate (Off-peak)"
                type="number"
                inputProps={{ step: '0.0001', min: 0 }}
                value={editForm.buy_rate_offpeak ?? ''}
                onChange={e => handleEditChange('buy_rate_offpeak', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Sell Rate (Off-peak)"
                type="number"
                inputProps={{ step: '0.0001', min: 0 }}
                value={editForm.sell_rate_offpeak ?? ''}
                onChange={e => handleEditChange('sell_rate_offpeak', e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Weekend & Fees
            </Typography>
          </Divider>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Sell Rate (Weekend)"
                type="number"
                inputProps={{ step: '0.0001', min: 0 }}
                value={editForm.sell_rate_weekend ?? ''}
                onChange={e => handleEditChange('sell_rate_weekend', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Connect Fee"
                type="number"
                inputProps={{ step: '0.0001', min: 0 }}
                value={editForm.connect_fee ?? ''}
                onChange={e => handleEditChange('connect_fee', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Billing Increment (s)"
                type="number"
                inputProps={{ step: '1', min: 1 }}
                value={editForm.billing_increment ?? ''}
                onChange={e => handleEditChange('billing_increment', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Min Duration (s)"
                type="number"
                inputProps={{ step: '1', min: 0 }}
                value={editForm.min_duration ?? ''}
                onChange={e => handleEditChange('min_duration', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Carrier (primary route)"
                value={(editForm as any).carrier_id ?? ''}
                onChange={e => setEditForm(prev => ({ ...prev, carrier_id: e.target.value } as any))}
              >
                <MenuItem value="">— none —</MenuItem>
                {carriers.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Backup carrier (tried next)"
                value={(editForm as any).backup_carrier_id ?? ''}
                onChange={e => setEditForm(prev => ({ ...prev, backup_carrier_id: e.target.value } as any))}
                helperText="Used if the primary carrier rejects or is full"
              >
                <MenuItem value="">— none —</MenuItem>
                {carriers.filter(c => String(c.id) !== String((editForm as any).carrier_id)).map(c => (
                  <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
